import React from "react";
import Text from "@/components/ui/text";
import { PersonData } from "@/entities/people/types";
import Badge from "@/components/ui/badge";
import { ArrowUpRight, Job, Mail, Phone } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import {
  EntityType,
  entityTypeToEntityName
} from "@/services/hooks/entities/useEntityType";
import { useNavigate } from "react-router-dom";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { useTranslation } from "react-i18next";
import { DrawerStateItem } from "@/services/hooks/entities/useQuickViewDrawer";
import { getFullName } from "@/entities/people/helpers";
import { map } from "lodash";
import ClickableLink from "@/components/ui/clickable-link";
import { IconButton } from "@/components/ui/icon-button";

interface Props {
  person: PersonData;
  mapping: string[] | undefined;
  openDrawer: (item: DrawerStateItem) => void;
}

const Person: React.FC<Props> = ({ person, mapping, openDrawer }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const url = `/${entityTypeToEntityName[EntityType.PERSON]}/${person.id}`;

  return (
    <div
      onClick={() => openDrawer(person)}
      className="relative flex w-full group items-start cursor-pointer transition-colors duration-200 ease-in-out rounded-xl hover:bg-white-4"
    >
      <div className="w-full min-w-0 p-2 flex flex-col gap-2">
        <div className="flex flex-wrap gap-2 pl-[6px] items-center min-w-0">
          <ClickableLink href={url} onClick={() => openDrawer(person)}>
            <Text
              variant="label"
              className="hover:text-link transition-colors ease-in-out duration-200 cursor-pointer"
              onClick={() => openDrawer(person)}
            >
              {getFullName(person)}
            </Text>
          </ClickableLink>
          {mapping &&
            map(mapping, (map, index) => (
              <Badge
                key={map + index}
                text={map}
                textVariant="bodySecondary"
                className="py-1 px-[6px]"
              />
            ))}
        </div>
        <div className="flex flex-col gap-1">
          {person?.jobTitle && (
            <Badge
              text={person?.jobTitle}
              textVariant="bodySecondary"
              textClassName="truncate min-w-0"
              className="py-0 px-[6px] border-none bg-transparent max-w-full min-w-0"
              icon={
                <IconContainer color="secondary">
                  <Job />
                </IconContainer>
              }
            />
          )}
          {map(person?.emails, (email, index) => (
            <Badge
              key={email + index}
              text={email}
              textVariant="bodySecondary"
              textClassName="truncate min-w-0"
              className="min-h-6 py-0 px-[6px] border-none bg-transparent max-w-full min-w-0"
              icon={
                <IconContainer color="secondary">
                  <Mail />
                </IconContainer>
              }
              copyable
              copyLabels={{
                tooltip: t("copyEmailTooltip"),
                copied: t("copied")
              }}
            />
          ))}
          {person?.phone && (
            <Badge
              text={person?.phone}
              textVariant="bodySecondary"
              textClassName="truncate min-w-0"
              className="min-h-6 py-0 px-[6px] border-none bg-transparent max-w-full min-w-0"
              icon={
                <IconContainer color="secondary">
                  <Phone />
                </IconContainer>
              }
              copyable
              copyLabels={{
                tooltip: t("copyPhoneTooltip"),
                copied: t("copied")
              }}
            />
          )}
        </div>
      </div>
      <div className="absolute top-2 right-2 z-10 opacity-0 transition-opacity duration-200 ease-in-out group-hover:opacity-100">
        <Tooltip>
          <TooltipTrigger>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                navigate(url);
              }}
              variant="icon"
              size="md"
            >
              <ArrowUpRight />
            </IconButton>
          </TooltipTrigger>
          <TooltipContent>{t("detailedViewTooltip")}</TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
};

export default Person;
